import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  Tooltip,
  CircularProgress,
  Alert,
  Snackbar,
  Grid,
  MenuItem,
  FormControlLabel,
  Switch,
  FormControl,
  InputLabel,
  Select
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { Add, Refresh, Delete, Edit, ChevronLeft, ChevronRight, AddCircleOutline, RemoveCircleOutline } from '@mui/icons-material';
import {
  getServiceTypePricing,
  getServiceTypePricingByServiceType,
  createServiceTypePricing,
  updateServiceTypePricing,
  deleteServiceTypePricing,
  getServiceTypes
} from '../../services/fulfillment';

const Pricing = () => {
  const { t } = useTranslation(['fulfillment', 'common']);
  const [pricing, setPricing] = useState([]);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  const [customRowsInput, setCustomRowsInput] = useState('');
  const [customRowsActive, setCustomRowsActive] = useState(false);

  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const defaultTiers = () => [
    { max_items: 10, per_item_fee: '2.50' },
    { max_items: 50, per_item_fee: '1.50' },
    { max_items: 100, per_item_fee: '1.00' },
    { max_items: 500, per_item_fee: '0.50' },
    { max_items: null, per_item_fee: '0.30' }
  ];
  const defaultInspectionTiers = () => [
    { max_items: 5, fee: '1.00' },
    { max_items: 10, fee: '2.00' },
    { max_items: 20, fee: '3.00' },
    { max_items: null, fee: '4.00' }
  ];
  const [form, setForm] = useState({
    service_type_sid: '',
    base_fee: '',
    per_item_fee: '',
    item_fee_tiers: [],
    item_fee_cap: '',
    weight_fee_per_kg: '',
    weight_threshold_kg: '5.00',
    minimum_fee: '',
    inspection_fee_per_item: '',
    inspection_fee_tiers: [],
    extra_package_fee: '',
    is_active: true
  });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, item: null });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const totalPages = Math.ceil(totalItems / rowsPerPage) || 1;

  const loadServiceTypes = useCallback(async () => {
    try {
      const response = await getServiceTypes({ page_size: 100 });
      if (response.results) {
        setServiceTypes(response.results);
      } else if (Array.isArray(response)) {
        setServiceTypes(response);
      }
    } catch (error) {
      console.error('Error loading service types:', error);
    }
  }, []);

  const loadPricing = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: page + 1,
        page_size: rowsPerPage
      };
      const response = await getServiceTypePricing(params);
      if (response.results) {
        setPricing(response.results);
        setTotalItems(response.count || 0);
      } else if (Array.isArray(response)) {
        setPricing(response);
        setTotalItems(response.length);
      }
    } catch (error) {
      console.error('Error loading pricing:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || t('fulfillment:pricing.messages.failed'),
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, t]);

  useEffect(() => {
    loadServiceTypes();
  }, [loadServiceTypes]);

  useEffect(() => {
    loadPricing();
  }, [loadPricing]);

  const handleOpen = async (item = null) => {
    if (item) {
      setEditId(item.sid);
      const tiers = Array.isArray(item.item_fee_tiers) && item.item_fee_tiers.length > 0
        ? item.item_fee_tiers.map((t) => ({
            max_items: t.max_items ?? '',
            per_item_fee: t.per_item_fee != null ? String(t.per_item_fee) : ''
          }))
        : [];
      const inspectionTiers = Array.isArray(item.inspection_fee_tiers) && item.inspection_fee_tiers.length > 0
        ? item.inspection_fee_tiers.map((t) => ({
            max_items: t.max_items ?? '',
            fee: t.fee != null ? String(t.fee) : ''
          }))
        : [];
      setForm({
        service_type_sid: item.service_type?.sid || '',
        base_fee: item.base_fee || '',
        per_item_fee: item.per_item_fee ?? '',
        item_fee_tiers: tiers,
        item_fee_cap: item.item_fee_cap ?? '',
        weight_fee_per_kg: item.weight_fee_per_kg || '',
        weight_threshold_kg: item.weight_threshold_kg || '5.00',
        minimum_fee: item.minimum_fee || '',
        inspection_fee_per_item: item.inspection_fee_per_item ?? '',
        inspection_fee_tiers: inspectionTiers,
        extra_package_fee: item.extra_package_fee ?? '',
        is_active: item.is_active !== undefined ? item.is_active : true
      });
    } else {
      setEditId(null);
      setForm({
        service_type_sid: '',
        base_fee: '',
        per_item_fee: '',
        item_fee_tiers: defaultTiers(),
        item_fee_cap: '500',
        weight_fee_per_kg: '',
        weight_threshold_kg: '5.00',
        minimum_fee: '',
        inspection_fee_per_item: '',
        inspection_fee_tiers: defaultInspectionTiers(),
        extra_package_fee: '',
        is_active: true
      });
    }
    setDialogOpen(true);
  };

  const handleClose = () => {
    setDialogOpen(false);
    setEditId(null);
    setForm({
      service_type_sid: '',
      base_fee: '',
      per_item_fee: '',
      item_fee_tiers: [],
      item_fee_cap: '',
      weight_fee_per_kg: '',
      weight_threshold_kg: '5.00',
      minimum_fee: '',
      inspection_fee_per_item: '',
      inspection_fee_tiers: [],
      extra_package_fee: '',
      is_active: true
    });
  };

  const addTier = () => {
    setForm((prev) => ({
      ...prev,
      item_fee_tiers: [...prev.item_fee_tiers, { max_items: '', per_item_fee: '' }]
    }));
  };
  const removeTier = (index) => {
    setForm((prev) => ({
      ...prev,
      item_fee_tiers: prev.item_fee_tiers.filter((_, i) => i !== index)
    }));
  };
  const updateTier = (index, field, value) => {
    setForm((prev) => {
      const next = [...prev.item_fee_tiers];
      next[index] = { ...next[index], [field]: value === '' || value == null ? (field === 'max_items' ? null : '') : value };
      return { ...prev, item_fee_tiers: next };
    });
  };
  const hasTieredPricing = () => {
    const tiers = form.item_fee_tiers || [];
    return tiers.length > 0 && tiers.some((t) => t.per_item_fee !== '' && t.per_item_fee != null);
  };
  const addInspectionTier = () => {
    setForm((prev) => ({
      ...prev,
      inspection_fee_tiers: [...prev.inspection_fee_tiers, { max_items: '', fee: '' }]
    }));
  };
  const removeInspectionTier = (index) => {
    setForm((prev) => ({
      ...prev,
      inspection_fee_tiers: prev.inspection_fee_tiers.filter((_, i) => i !== index)
    }));
  };
  const updateInspectionTier = (index, field, value) => {
    setForm((prev) => {
      const next = [...prev.inspection_fee_tiers];
      next[index] = { ...next[index], [field]: value === '' || value == null ? (field === 'max_items' ? null : '') : value };
      return { ...prev, inspection_fee_tiers: next };
    });
  };

  const handleSave = async () => {
    if (!form.service_type_sid || !form.base_fee || !form.minimum_fee) {
      setSnackbar({
        open: true,
        message: t('common:validation.required'),
        severity: 'error'
      });
      return;
    }
    const useTiers = hasTieredPricing();
    if (!useTiers && (form.per_item_fee === '' || form.per_item_fee == null)) {
      setSnackbar({
        open: true,
        message: 'Provide either Per item fee (flat) or Tiered item fee rows.',
        severity: 'error'
      });
      return;
    }
    if (useTiers) {
      const invalid = (form.item_fee_tiers || []).some((t) => !t.per_item_fee || t.per_item_fee === '');
      if (invalid) {
        setSnackbar({
          open: true,
          message: 'Each tier must have a per-item fee.',
          severity: 'error'
        });
        return;
      }
    }

    try {
      const payload = {
        service_type_sid: form.service_type_sid,
        base_fee: parseFloat(form.base_fee),
        weight_fee_per_kg: parseFloat(form.weight_fee_per_kg) || 0,
        weight_threshold_kg: parseFloat(form.weight_threshold_kg) || 5.00,
        minimum_fee: parseFloat(form.minimum_fee),
        inspection_fee_per_item: form.inspection_fee_per_item ? parseFloat(form.inspection_fee_per_item) : null,
        inspection_fee_tiers: (form.inspection_fee_tiers || [])
          .filter((t) => t.fee !== '' && t.fee != null)
          .map((t) => ({
            max_items: t.max_items === '' || t.max_items == null ? null : parseInt(t.max_items, 10),
            fee: String(t.fee)
          })),
        extra_package_fee: form.extra_package_fee ? parseFloat(form.extra_package_fee) : 0,
        is_active: form.is_active
      };
      if (useTiers) {
        payload.item_fee_tiers = (form.item_fee_tiers || []).map((t) => ({
          max_items: t.max_items === '' || t.max_items == null ? null : parseInt(t.max_items, 10),
          per_item_fee: String(t.per_item_fee)
        }));
        payload.item_fee_cap = form.item_fee_cap ? parseFloat(form.item_fee_cap) : null;
        payload.per_item_fee = null;
      } else {
        payload.per_item_fee = parseFloat(form.per_item_fee);
        payload.item_fee_tiers = [];
        payload.item_fee_cap = form.item_fee_cap ? parseFloat(form.item_fee_cap) : null;
      }

      if (editId) {
        await updateServiceTypePricing(editId, payload);
        setSnackbar({
          open: true,
          message: t('fulfillment:pricing.messages.updated'),
          severity: 'success'
        });
      } else {
        await createServiceTypePricing(payload);
        setSnackbar({
          open: true,
          message: t('fulfillment:pricing.messages.created'),
          severity: 'success'
        });
      }
      handleClose();
      loadPricing();
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.error || t('fulfillment:pricing.messages.failed'),
        severity: 'error'
      });
    }
  };

  const handleDeleteClick = (item) => {
    setDeleteDialog({ open: true, item });
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteServiceTypePricing(deleteDialog.item.sid);
      setSnackbar({
        open: true,
        message: t('fulfillment:pricing.messages.deleted'),
        severity: 'success'
      });
      setDeleteDialog({ open: false, item: null });
      loadPricing();
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.response?.data?.error || t('fulfillment:pricing.messages.failed'),
        severity: 'error'
      });
    }
  };

  const handlePrevPage = () => {
    if (page > 0) {
      setPage(page - 1);
    }
  };

  const handleNextPage = () => {
    if (page < totalPages - 1) {
      setPage(page + 1);
    }
  };

  const handleRowsPerPageChange = (e) => {
    const value = e.target.value;
    if (value === 'custom') {
      setCustomRowsActive(true);
      setCustomRowsInput(rowsPerPage.toString());
    } else {
      setRowsPerPage(Number(value));
      setPage(0);
      setCustomRowsActive(false);
    }
  };

  const handleCustomRowsInputChange = (e) => {
    setCustomRowsInput(e.target.value.replace(/\D/g, ''));
  };

  const handleCustomRowsInputBlur = () => {
    const val = parseInt(customRowsInput, 10);
    if (val && val > 0) {
      setRowsPerPage(val);
      setPage(0);
    }
    setCustomRowsActive(false);
  };

  const addSerialNumbers = (data, page, rowsPerPage) =>
    data.map((row, idx) => ({
      ...row,
      sn: (page * rowsPerPage) + idx + 1
    }));

  const columns = [
    { field: 'sn', headerName: t('fulfillment:table.sn'), width: 80, sortable: false, filterable: false },
    {
      field: 'service_type',
      headerName: t('fulfillment:pricing.serviceType'),
      flex: 1,
      minWidth: 150,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.row.service_type?.name || 'N/A'}
        </Typography>
      )
    },
    {
      field: 'base_fee',
      headerName: t('fulfillment:pricing.baseFee'),
      width: 120,
      renderCell: (params) => (
        <Typography variant="body2">
          ¥{Number(params.value).toFixed(2)}
        </Typography>
      )
    },
    {
      field: 'per_item_fee',
      headerName: t('fulfillment:pricing.perItemFee'),
      width: 120,
      renderCell: (params) => {
        const tiers = params.row.item_fee_tiers;
        const useTiers = Array.isArray(tiers) && tiers.length > 0;
        if (useTiers) {
          return (
            <Typography variant="body2" color="primary" fontWeight="medium">
              Tiered
            </Typography>
          );
        }
        return (
          <Typography variant="body2">
            ¥{Number(params.value ?? 0).toFixed(2)}
          </Typography>
        );
      }
    },
    {
      field: 'weight_fee_per_kg',
      headerName: t('fulfillment:pricing.weightFeePerKg'),
      width: 140,
      renderCell: (params) => (
        <Typography variant="body2">
          ¥{Number(params.value).toFixed(2)}/kg
        </Typography>
      )
    },
    {
      field: 'weight_threshold_kg',
      headerName: t('fulfillment:pricing.weightThreshold'),
      width: 140,
      renderCell: (params) => (
        <Typography variant="body2">
          {Number(params.value).toFixed(2)} kg
        </Typography>
      )
    },
    {
      field: 'minimum_fee',
      headerName: t('fulfillment:pricing.minimumFee'),
      width: 120,
      renderCell: (params) => (
        <Typography variant="body2">
          ¥{Number(params.value).toFixed(2)}
        </Typography>
      )
    },
    {
      field: 'inspection_fee_per_item',
      headerName: t('fulfillment:pricing.inspectionFeePerItem'),
      width: 160,
      renderCell: (params) => (
        <Typography variant="body2">
          {params.value ? `¥${Number(params.value).toFixed(2)}` : 'N/A'}
        </Typography>
      )
    },
    {
      field: 'inspection_fee_tiers',
      headerName: 'Inspection thresholds',
      width: 180,
      renderCell: (params) => {
        const tiers = Array.isArray(params.value) ? params.value : [];
        if (!tiers.length) {
          return <Typography variant="body2">N/A</Typography>;
        }
        const first = tiers[0];
        const last = tiers[tiers.length - 1];
        return (
          <Typography variant="body2" color="primary" fontWeight="medium">
            {tiers.length} tiers, RMB {first.fee} - {last.fee}
          </Typography>
        );
      }
    },
    {
      field: 'extra_package_fee',
      headerName: 'Extra package fee',
      width: 150,
      renderCell: (params) => (
        <Typography variant="body2">
          RMB {Number(params.value ?? 0).toFixed(2)}
        </Typography>
      )
    },
    {
      field: 'is_active',
      headerName: t('fulfillment:pricing.isActive'),
      width: 100,
      renderCell: (params) => (
        <Chip
          label={params.value ? t('common:status.active') : t('common:status.inactive')}
          size="small"
          sx={{
            backgroundColor: params.value ? '#4CAF50' : '#F44336',
            color: 'white',
            fontWeight: 'bold'
          }}
        />
      )
    },
    {
      field: 'actions',
      headerName: t('fulfillment:table.actions'),
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title={t('common:edit')}>
            <IconButton color="primary" onClick={() => handleOpen(params.row)}>
              <Edit />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('common:delete')}>
            <IconButton color="error" onClick={() => handleDeleteClick(params.row)}>
              <Delete />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

  return (
    <Box sx={{
      minHeight: '100vh',
      backgroundColor: 'background.default',
      px: { xs: 0.75, sm: 1, md: 1.5 },
      py: { xs: 2, sm: 2.5, md: 3 },
      maxWidth: '95vw',
      overflowX: 'hidden'
    }}>
      {/* Header */}
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 3,
        flexWrap: 'wrap',
        gap: 2
      }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#DC143C' }}>
          {t('fulfillment:pricing.title')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={loadPricing}
            disabled={loading}
            sx={{ minWidth: 'auto' }}
          >
            {t('common:refresh')}
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            sx={{ backgroundColor: '#DC143C', '&:hover': { backgroundColor: '#B91C3C' }, minWidth: 'auto' }}
            onClick={() => handleOpen()}
          >
            {t('fulfillment:pricing.add')}
          </Button>
        </Box>
      </Box>

      {/* Table */}
      <Card sx={{ backgroundColor: 'background.paper' }}>
        <CardContent sx={{ p: 0 }}>
          <Box sx={{
            width: '100%',
            overflowX: 'auto',
            minHeight: 520,
            '&::-webkit-scrollbar': {
              height: 8,
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'rgba(0,0,0,0.1)',
              borderRadius: 4,
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'rgba(0,0,0,0.3)',
              borderRadius: 4,
            },
            '&::-webkit-scrollbar-thumb:hover': {
              backgroundColor: 'rgba(0,0,0,0.5)',
            },
          }}>
            {pricing.length === 0 && !loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', flexDirection: 'column' }}>
                <Typography variant="h6" color="textSecondary" sx={{ mb: 2 }}>
                  {t('fulfillment:table.noData')}
                </Typography>
                <Button variant="outlined" onClick={loadPricing}>
                  {t('common:refresh')}
                </Button>
              </Box>
            ) : (
              <DataGrid
                rows={addSerialNumbers(pricing, page, rowsPerPage)}
                columns={columns}
                getRowId={(row) => row.sid}
                pageSize={rowsPerPage}
                rowsPerPageOptions={[]}
                disableSelectionOnClick
                loading={loading}
                hideFooter
                sx={{
                  border: 'none',
                  minWidth: 1450,
                  '& .MuiDataGrid-columnHeader': {
                    backgroundColor: '#DC143C',
                  },
                  '& .MuiDataGrid-columnHeaderTitle': {
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                  },
                  '& .MuiDataGrid-columnHeader .MuiSvgIcon-root': {
                    color: 'white'
                  },
                  '& .MuiDataGrid-row:hover': {
                    backgroundColor: '#FFF0F3'
                  },
                  '& .MuiDataGrid-cell': {
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    padding: { xs: '8px 4px', sm: '8px 12px' }
                  }
                }}
              />
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Pagination */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: { xs: 'center', sm: 'flex-end' },
        mt: 3,
        gap: 2,
        flexWrap: 'wrap'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={handlePrevPage} disabled={page === 0}>
            <ChevronLeft />
          </IconButton>
          <Typography variant="body2">
            {t('common:pagination.page')} {totalPages === 0 ? 0 : page + 1} {t('common:pagination.of')} {totalPages}
          </Typography>
          <IconButton onClick={handleNextPage} disabled={page >= totalPages - 1}>
            <ChevronRight />
          </IconButton>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2">
            {t('common:pagination.rowsPerPage')}
          </Typography>
          {!customRowsActive ? (
            <TextField
              select
              size="small"
              value={[10, 20, 50, 100].includes(rowsPerPage) ? rowsPerPage : 'custom'}
              onChange={handleRowsPerPageChange}
              sx={{ width: 100 }}
            >
              {[10, 20, 50, 100].map((option) => (
                <MenuItem key={option} value={option}>{option}</MenuItem>
              ))}
              <MenuItem value="custom">{t('common:pagination.custom')}</MenuItem>
            </TextField>
          ) : (
            <TextField
              size="small"
              value={customRowsInput}
              onChange={handleCustomRowsInputChange}
              onBlur={handleCustomRowsInputBlur}
              autoFocus
              sx={{ width: 100 }}
              inputProps={{ min: 1, style: { textAlign: 'center' } }}
              placeholder={t('common:pagination.rows')}
            />
          )}
        </Box>
      </Box>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ color: '#DC143C', fontWeight: 'bold' }}>
          {editId ? t('fulfillment:pricing.edit') : t('fulfillment:pricing.add')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <FormControl fullWidth required>
              <InputLabel>{t('fulfillment:pricing.serviceType')}</InputLabel>
              <Select
                value={form.service_type_sid}
                onChange={(e) => setForm({ ...form, service_type_sid: e.target.value })}
                label={t('fulfillment:pricing.serviceType')}
                disabled={!!editId}
              >
                {serviceTypes.map((st) => (
                  <MenuItem key={st.sid} value={st.sid}>
                    {st.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label={t('fulfillment:pricing.baseFee')}
                  type="number"
                  value={form.base_fee}
                  onChange={(e) => setForm({ ...form, base_fee: e.target.value })}
                  required
                  fullWidth
                  inputProps={{ step: '0.01', min: '0' }}
                  helperText="RMB"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label={t('fulfillment:pricing.perItemFee')}
                  type="number"
                  value={form.per_item_fee}
                  onChange={(e) => setForm({ ...form, per_item_fee: e.target.value })}
                  fullWidth
                  inputProps={{ step: '0.01', min: '0' }}
                  helperText="RMB per item (flat; leave empty if using tiers below)"
                />
              </Grid>
              {/* Tiered item fee + cap */}
              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Tiered item fee (optional)
                </Typography>
                <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 1 }}>
                  If set, replaces flat per-item fee. First tier: 1–10 @ ¥2.50, next 11–50 @ ¥1.50, etc. Leave max items empty for no limit.
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {(form.item_fee_tiers || []).map((tier, index) => (
                    <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                      <TextField
                        label="Up to items"
                        type="number"
                        value={tier.max_items ?? ''}
                        onChange={(e) => updateTier(index, 'max_items', e.target.value === '' ? null : e.target.value)}
                        size="small"
                        sx={{ width: 120 }}
                        inputProps={{ min: 1 }}
                        placeholder="∞"
                      />
                      <TextField
                        label="Per item (¥)"
                        type="number"
                        value={tier.per_item_fee ?? ''}
                        onChange={(e) => updateTier(index, 'per_item_fee', e.target.value)}
                        size="small"
                        sx={{ width: 120 }}
                        inputProps={{ step: '0.01', min: '0' }}
                      />
                      <IconButton size="small" color="error" onClick={() => removeTier(index)} aria-label="Remove tier">
                        <RemoveCircleOutline />
                      </IconButton>
                    </Box>
                  ))}
                  <Button size="small" startIcon={<AddCircleOutline />} onClick={addTier}>
                    Add tier
                  </Button>
                </Box>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Item fee cap (¥)"
                  type="number"
                  value={form.item_fee_cap}
                  onChange={(e) => setForm({ ...form, item_fee_cap: e.target.value })}
                  fullWidth
                  inputProps={{ step: '0.01', min: '0' }}
                  helperText="Max total item fee per request (optional)"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label={t('fulfillment:pricing.weightFeePerKg')}
                  type="number"
                  value={form.weight_fee_per_kg}
                  onChange={(e) => setForm({ ...form, weight_fee_per_kg: e.target.value })}
                  fullWidth
                  inputProps={{ step: '0.01', min: '0' }}
                  helperText="RMB per kg (over threshold)"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label={t('fulfillment:pricing.weightThreshold')}
                  type="number"
                  value={form.weight_threshold_kg}
                  onChange={(e) => setForm({ ...form, weight_threshold_kg: e.target.value })}
                  fullWidth
                  inputProps={{ step: '0.01', min: '0' }}
                  helperText="kg (default: 5kg)"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label={t('fulfillment:pricing.minimumFee')}
                  type="number"
                  value={form.minimum_fee}
                  onChange={(e) => setForm({ ...form, minimum_fee: e.target.value })}
                  required
                  fullWidth
                  inputProps={{ step: '0.01', min: '0' }}
                  helperText="RMB"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label={t('fulfillment:pricing.inspectionFeePerItem')}
                  type="number"
                  value={form.inspection_fee_per_item}
                  onChange={(e) => setForm({ ...form, inspection_fee_per_item: e.target.value })}
                  fullWidth
                  inputProps={{ step: '0.01', min: '0' }}
                  helperText="RMB per item (for inspect & package only)"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Extra package fee"
                  type="number"
                  value={form.extra_package_fee}
                  onChange={(e) => setForm({ ...form, extra_package_fee: e.target.value })}
                  fullWidth
                  inputProps={{ step: '0.01', min: '0' }}
                  helperText="RMB for each carton/package after the first"
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                  Inspection fee thresholds
                </Typography>
                <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 1 }}>
                  Flat inspection fee by item count. Example: 1-5 items = RMB 1, 6-10 = RMB 2. Leave max items empty for no limit.
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {(form.inspection_fee_tiers || []).map((tier, index) => (
                    <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                      <TextField
                        label="Up to items"
                        type="number"
                        value={tier.max_items ?? ''}
                        onChange={(e) => updateInspectionTier(index, 'max_items', e.target.value === '' ? null : e.target.value)}
                        size="small"
                        sx={{ width: 140 }}
                        inputProps={{ min: 1 }}
                        placeholder="No limit"
                      />
                      <TextField
                        label="Fee (RMB)"
                        type="number"
                        value={tier.fee ?? ''}
                        onChange={(e) => updateInspectionTier(index, 'fee', e.target.value)}
                        size="small"
                        sx={{ width: 140 }}
                        inputProps={{ step: '0.01', min: '0' }}
                      />
                      <IconButton size="small" color="error" onClick={() => removeInspectionTier(index)} aria-label="Remove inspection tier">
                        <RemoveCircleOutline />
                      </IconButton>
                    </Box>
                  ))}
                  <Button size="small" startIcon={<AddCircleOutline />} onClick={addInspectionTier}>
                    Add inspection threshold
                  </Button>
                </Box>
              </Grid>
            </Grid>

            <FormControlLabel
              control={
                <Switch
                  checked={form.is_active}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                />
              }
              label={t('fulfillment:pricing.isActive')}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>{t('common:cancel')}</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            sx={{ backgroundColor: '#DC143C', '&:hover': { backgroundColor: '#B91C3C' } }}
          >
            {t('common:save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, item: null })} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: '#DC143C', fontWeight: 'bold' }}>
          {t('fulfillment:common.confirmDelete')}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            {t('fulfillment:common.deleteWarning')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, item: null })}>
            {t('common:cancel')}
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            sx={{ backgroundColor: '#DC143C', '&:hover': { backgroundColor: '#B91C3C' } }}
          >
            {t('common:delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Pricing;
